import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import CountUp from "react-countup";
import { useTimelineContext } from "../contexts/TimelineContext";
import useSmoothScroll from "../hooks/useSmoothScroll";
import { HOVER_ANIMATIONS } from "../constants/animations";
import { BUTTON_STYLES } from "../constants/styles";
import InteractiveProfileImage from "./InteractiveProfileImage";

function Header() {
  return (
    <div className="relative z-10">
      <HeaderContent />
    </div>
  );
}

// Animated Counter Component using react-countup
const AnimatedCounter = ({ end, delay = 300 }: { end: number; delay?: number }) => {
  const prefersReducedMotion = useReducedMotion();
  const [startAnimation, setStartAnimation] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStartAnimation(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  if (prefersReducedMotion) {
    return <span>{end.toLocaleString()}+</span>;
  }

  // Reserve space for the full number to prevent layout shifts
  const maxWidth = end.toLocaleString().length;
  return (
    <span
      style={{
        display: 'inline-block',
        minWidth: `${maxWidth * 0.62}em`,
        textAlign: 'center'
      }}
    >
      <CountUp
        //start={0}
        end={startAnimation ? end : 0}
        duration={1.8}
        separator=","
        suffix="+"
      />
    </span>
  );
};

function HeaderContent() {
  const [showContent, setShowContent] = useState<boolean>(false);
  const [showButtons, setShowButtons] = useState<boolean>(false);
  const scrollToElement = useSmoothScroll();
  const { setActiveCategory } = useTimelineContext();

  useEffect(() => {
    const contentTimer = setTimeout(() => setShowContent(true), 150);
    const buttonsTimer = setTimeout(() => setShowButtons(true), 600);

    return () => {
      clearTimeout(contentTimer);
      clearTimeout(buttonsTimer);
    };
  }, []);

  const handleViewProjects = () => {
    scrollToElement("experience");
    setActiveCategory("projects");
  };

  const handleContactMe = () => {
    scrollToElement("contact");
  };

  return (
    <div className="duration-300 relative z-10 flex flex-col items-center justify-center min-h-screen px-4 pt-28 pb-20 md:pt-32">
      <InteractiveProfileImage
        src={require("../images/profile/portrait-final.jpeg")}
        alt="Tomer Rosenfeld - Full Stack Developer"
        className="mb-12"
      />

      <div className="text-center max-w-5xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight dark:text-surface-50 text-accent-900 mb-8 leading-tight font-display"
        >
          The Developer Behind{" "}
          <span className="text-primary-600 dark:text-primary-400 font-extrabold">
            <AnimatedCounter end={20000000} />
          </span>{" "}
          Downloads
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="text-lg md:text-xl lg:text-2xl dark:text-accent-300 text-accent-600 mb-6 font-light"
        >
          Full-Stack Engineer • 8200 Veteran • Founder with an Exit
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="text-base md:text-lg dark:text-accent-400 text-accent-600 mb-12 max-w-2xl mx-auto leading-relaxed"
        >
          I take products from idea to production: mobile apps, AI backends, and the infrastructure behind them.
        </motion.p>
      </div>
      <motion.div
        className="flex flex-col sm:flex-row gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={showButtons ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="w-full sm:w-auto flex justify-center">
          <motion.button
            onClick={handleViewProjects}
            {...HOVER_ANIMATIONS.scale}
            className={`${BUTTON_STYLES.primary} w-full sm:w-auto`}
          >
            View My Work
          </motion.button>
        </div>
        <div className="w-full sm:w-auto flex justify-center">
          <motion.button
            onClick={handleContactMe}
            {...HOVER_ANIMATIONS.scale}
            className={`${BUTTON_STYLES.secondary} w-full sm:w-auto`}
          >
            Hire Me
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}

export default Header;
